import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../../../hooks/useScrollReveal';
import cementBanner from '../../../assets/cement banner.jpg';
import buildingImg  from '../../../assets/building.jpg';

// Machine images for Key Machines section
import vtlImg  from '../../../assets/miltech machine bg remover/VTL-removebg-preview.jpeg';
import kolbImg from '../../../assets/miltech machine bg remover/kolb-u2013-germany 8.2m dia.jpeg';
import vmcImg  from '../../../assets/miltech machine bg remover/VMC-1-removebg-preview.png';

import './cement.css';

const CAPABILITIES = [
  { title: 'Impeller Machining',         desc: 'High-wear-resistant impellers for cement mills, blowers, and pumps — machined to tight tolerances.' },
  { title: 'Valve Body Fabrication',      desc: 'Precision valve bodies for high-pressure systems in cement and thermal plant applications.' },
  { title: 'Pressure Vessel Manufacture', desc: 'Heavy-gauge pressure vessels and tanks for thermal power plants and cement machinery.' },
  { title: 'Heavy Forging Alloys',        desc: 'Machining of heavy forged alloy components — earth-moving parts, crusher components, and mill liners.' },
  { title: 'Earth-Moving Equipment',      desc: 'Structural fabrication and machining for earth-moving equipment components and assemblies.' },
  { title: 'Surface Treatment',           desc: 'Zinc primer, painting, and surface treatment for all fabricated components before delivery.' },
];

const STATS = [
  { val: '100T', label: 'Max Machine Capacity' },
  { val: 'Ø 8.2m', label: 'VTL Swing Diameter' },
  { val: '25+', label: 'Years Experience' },
  { val: 'ISO', label: '9001:2015 Certified' },
];

// Key machines WITH images
const KEY_MACHINES = [
  {
    name: 'BERTHIEZ CNC VTL',
    spec: 'Ø 8200mm swing · 100 Ton capacity',
    color: '#F59E0B',
    img: vtlImg,
    desc: 'France-made Vertical Turning Lathe — the backbone of our large-diameter industrial machining.',
    origin: 'BERTHIEZ, France',
  },
  {
    name: 'KOLB CNC Gantry',
    spec: 'X-Axis 14000mm · Y: 4000mm · Z: 3500mm',
    color: '#E8192C',
    img: kolbImg,
    desc: 'Germany-made gantry floor boring machine — ideal for large frame and bed components.',
    origin: 'KOLB, Germany',
  },
  {
    name: 'VMC with 4th Axis',
    spec: 'Table 2000×1000×1000 · 4th Axis',
    color: '#1E3A8A',
    img: vmcImg,
    desc: 'Vertical Machining Centres for precision milling, drilling, and 3D contouring work.',
    origin: 'DAHLIH, Taiwan',
  },
];

export default function Cement() {
  const bodyRef = useScrollReveal();
  const capRef  = useScrollReveal();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="cement-page">

      {/* ── HERO BANNER ── */}
      <section className="cm-hero">
        <img src={cementBanner} alt="Cement Machinery" className="cm-hero__img" />
        <div className="cm-hero__overlay" />
        <div className="container cm-hero__content">
          <div className="cm-hero__tag">
            <span className="cm-hero__tag-dot" />
            Heavy Industrial Machining
          </div>
          <h1 className="cm-hero__title">
            Cement<br/>
            <span className="cm-hero__title-accent">Machinery</span>
          </h1>
          <p className="cm-hero__sub">
            Robust fabrication and CNC machining for cement plant equipment, thermal power plants,
            and heavy industrial applications — since 2002.
          </p>
          <div className="cm-hero__pills">
            <span className="cm-hero__pill">BERTHIEZ VTL 8.2m</span>
            <span className="cm-hero__pill">KOLB Gantry 12.5m</span>
            <span className="cm-hero__pill">Heavy Fabrication</span>
            <span className="cm-hero__pill">ISO 9001:2015</span>
          </div>
        </div>
        <div className="cm-hero__wave">
          <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="var(--bg)"/>
          </svg>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="cm-stats-section">
        <div className="container">
          <div className="cm-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="cm-stat-card">
                <span className="cm-stat-val">{s.val}</span>
                <span className="cm-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRO SECTION ── */}
      <section className="section bg-white" ref={bodyRef}>
        <div className="container">
          <div className="cm-intro-grid">
            <div className="reveal from-left">
              
              <h2 className="section-title">
                Precision Parts for<br/>
                <span style={{ color: 'var(--amber)' }}>Heavy Industry</span>
              </h2>
              <div className="line-red" style={{ background: 'var(--amber)' }} />
              <p className="section-sub">
                MILLTECH CNC has decades of experience in heavy industrial machining for cement plants,
                thermal power facilities, and earth-moving equipment manufacturers. Our BERTHIEZ 8.2m VTL
                and KOLB 12.5m Gantry machine handle the largest components in the industry.
              </p>
              <p className="section-sub">
                From high-pressure vessels and valve bodies to impellers and heavy forgings — every component
                is machined under rigorous quality control with full dimensional traceability and surface treatment.
              </p>
              <Link to="/contact" className="btn btn-indigo" style={{ marginTop: '2rem' }}>
                Request Industrial Quote →
              </Link>
            </div>
            <div className="cm-intro-img reveal from-right">
              <img src={buildingImg} alt="Industrial manufacturing" />
              <div className="cm-intro-img__badge">
                <span>Heavy</span>
                <span>Engineering</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES ── */}
      <section className="section" ref={capRef} style={{ background: 'var(--bg-3)' }}>
        <div className="container">
    
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            Cement & Industrial Capabilities
          </h2>
          <div className="cm-cap-grid stagger">
            {CAPABILITIES.map((c, i) => (
              <div key={i} className="cm-cap-card reveal">
                <div className="cm-cap-card__accent" style={{ background: 'var(--amber)' }} />
                <h3 className="cm-cap-card__title">{c.title}</h3>
                <p className="cm-cap-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── KEY MACHINES — WITH IMAGES ── */}
      <section className="section bg-white">
        <div className="container">
            
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            Key Machines for Industrial Work
          </h2>
          <div className="cm-machine-grid">
            {KEY_MACHINES.map((m, i) => (
              <div key={i} className="cm-machine-card" style={{ '--cm': m.color }}>
                {/* Machine image */}
                <div className="cm-machine-card__img-wrap">
                  <img src={m.img} alt={m.name} className="cm-machine-card__img" />
                </div>
                <div className="cm-machine-card__content">
                  <div className="cm-machine-card__dot" />
                  <h3 className="cm-machine-card__name">{m.name}</h3>
                  <span className="cm-machine-card__spec">{m.spec}</span>
                  <span className="cm-machine-card__origin">{m.origin}</span>
                  <p className="cm-machine-card__desc">{m.desc}</p>
                  <Link to="/machines" className="cm-machine-card__link">
                    View Machine Specs →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cm-cta">
        <div className="container cm-cta__inner">
          <div>
            <h2 className="cm-cta__title">Heavy Component Requirements?</h2>
            <p className="cm-cta__sub">Share your drawings — our engineers respond within 24 hours.</p>
          </div>
          <Link to="/contact" className="btn btn-primary">Contact Us</Link>
        </div>
      </section>

    </main>
  );
}